export const ANNOTATION_APPEARANCE_STORAGE_KEY = 'kairos.annotationAppearance';
export const ANNOTATION_APPEARANCE_PREF_NAMESPACE = 'viewer.annotation_appearance';

export const DEFAULT_ANNOTATION_APPEARANCE = Object.freeze({
  color: '#ff9800',
  lineWidth: 2,
  fontSize: 12,
  fontFamily: 'monospace',
  showLabels: true,
});

// Store the compact preference key (monospace/sans/serif), not the CSS stack,
// so future desktop/mobile clients can map to their own safe font tokens.
export const ANNOTATION_FONT_FAMILIES = Object.freeze({
  monospace: 'monospace',
  sans: 'Inter, Arial, sans-serif',
  serif: 'Georgia, serif',
});

const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
};

const safeJson = (raw) => {
  try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
};

const normalizeColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) return trimmed.toLowerCase();
  return fallback;
};

export const normalizeAnnotationAppearance = (value = {}) => {
  const source = value && typeof value === 'object' ? value : {};
  const defaults = DEFAULT_ANNOTATION_APPEARANCE;
  const familyKey = Object.prototype.hasOwnProperty.call(ANNOTATION_FONT_FAMILIES, source.fontFamily)
    ? source.fontFamily
    : defaults.fontFamily;
  return {
    color: normalizeColor(source.color, defaults.color),
    lineWidth: clampNumber(source.lineWidth, 1, 6, defaults.lineWidth),
    fontSize: clampNumber(source.fontSize, 8, 24, defaults.fontSize),
    fontFamily: familyKey,
    showLabels: typeof source.showLabels === 'boolean' ? source.showLabels : defaults.showLabels,
  };
};

export const cssFontFamilyForAnnotation = (fontFamilyKey) => (
  ANNOTATION_FONT_FAMILIES[fontFamilyKey] || ANNOTATION_FONT_FAMILIES[DEFAULT_ANNOTATION_APPEARANCE.fontFamily]
);

export const loadAnnotationAppearance = () => {
  if (typeof window === 'undefined') return DEFAULT_ANNOTATION_APPEARANCE;
  return normalizeAnnotationAppearance(safeJson(window.localStorage.getItem(ANNOTATION_APPEARANCE_STORAGE_KEY)));
};

export const saveAnnotationAppearance = (value) => {
  const normalized = normalizeAnnotationAppearance(value);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(ANNOTATION_APPEARANCE_STORAGE_KEY, JSON.stringify(normalized));
    } catch (_) {
      // Profile-backed save remains the source of truth; local fallback may be unavailable
      // in hardened/private browser modes. Keep the normalized UI state instead of throwing.
    }
  }
  return normalized;
};
